import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { filterActiveInMonth, statusLabel } from "@/lib/employeeScope";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS_IT, fmtHoursSigned, fmtHours, fmtNum, lastCompletedMonth, currentSemesterStart, COMPENSATION_PERIODS, compensationPeriod } from "@/lib/constants";
import { formatCounterHours } from "@/lib/counterHours";
import {
  FileText, FileSpreadsheet, Download, Activity, CalendarCheck2, ClipboardList,
  GraduationCap, ReceiptText, TableProperties, WalletCards, ContactRound,
  Search, Users, CalendarDays, LineChart, BarChart3, TrendingUp, TrendingDown, Printer,
  ShieldCheck, UserSearch, FileCheck2,
} from "lucide-react";
import CedolinoPreviewDialog from "@/components/CedolinoPreviewDialog";
import { SuiteCard, SuiteButton, SuiteEmployeePicker, SuiteMonthPicker } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { reconcileEmployeeWithCompany } from "@/lib/companyEmployeeCoherence";
import { announceShellCompany, companyNameForShell } from "@/lib/shellCompany";
import { downloadBlob as saveBlob } from "@/lib/download";
import { useEffectiveRole } from "@/foundation/context";

async function downloadBlob(url, params, filename) {
  const res = await api.get(url, { params, responseType: "blob" });
  const contentType = res.headers?.["content-type"] || "application/octet-stream";
  saveBlob(res.data, filename, contentType);
}

/* -------------------------------------------------------------------------- */
/*  Riga documento uniforme                                                    */
/* -------------------------------------------------------------------------- */
function DocRow({ icon: Icon, title, description, meta, tag, actions = [], testid }) {
  return (
    <div
      data-testid={testid}
      className="group grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-suite-neutral-200 bg-white px-3 py-2.5 transition-colors hover:border-suite-primary/40 hover:bg-suite-primary-light/10"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-suite-primary-light text-suite-primary">
        <Icon size={17} strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="truncate text-[13px] font-semibold text-suite-neutral-900">{title}</div>
          {tag && (
            <span className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-1.5 py-0.5 text-[10px] font-medium text-suite-neutral-600">
              {tag}
            </span>
          )}
        </div>
        {description && (
          <div className="mt-0.5 text-[11.5px] leading-4 text-suite-neutral-500 truncate">
            {description}
            {meta && <span className="ml-1.5 text-suite-neutral-400">· {meta}</span>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-1.5">
        {actions.map(({ key, label, ...btnProps }) => (
          <SuiteButton key={key || label} size="sm" {...btnProps}>
            {label}
          </SuiteButton>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Piccolo KPI compatto                                                       */
/* -------------------------------------------------------------------------- */
function Kpi({ label, value, unit, hint, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "bg-white border-suite-neutral-200 text-suite-neutral-800",
    primary: "bg-suite-primary-light/40 border-suite-primary/25 text-suite-primary",
    good: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    bad: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">{label}</div>
        {Icon && <Icon size={14} className="opacity-60" />}
      </div>
      <div className="mt-1 flex items-baseline gap-1 font-mono">
        <span className="text-[22px] font-bold leading-none">{value}</span>
        {unit && <span className="text-xs opacity-70">{unit}</span>}
      </div>
      {hint && <div className="mt-0.5 text-[10.5px] leading-4 opacity-70">{hint}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Barra segmento                                                             */
/* -------------------------------------------------------------------------- */
function Bar({ value = 0, max = 100, color = "bg-suite-primary" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 rounded-full bg-suite-neutral-100 overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Header sezione con azioni a destra                                         */
/* -------------------------------------------------------------------------- */
function SectionHeader({ icon: Icon, title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        {Icon && <Icon size={18} className="mt-0.5 text-suite-primary shrink-0" />}
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-suite-neutral-900">{title}</div>
          {subtitle && <div className="text-[12px] text-suite-neutral-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-1.5">{actions}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Reports Page                                                          */
/* -------------------------------------------------------------------------- */
const NAV_TABS = [
  { key: "monthly", label: "Report mensile", icon: CalendarCheck2 },
  { key: "company", label: "Riepilogo aziendale", icon: TableProperties, hideEmployee: true },
  { key: "absenteeism", label: "Assenteismo e presenza", icon: Activity, hideEmployee: true },
  { key: "semester", label: "Periodo banca ore", icon: WalletCards },
  { key: "safety", label: "Sicurezza sul lavoro", icon: ShieldCheck, hideEmployee: true },
];

export default function Reports() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(
    effectiveRole === "employee" ? user.employee_id : "__all__"
  );
  const _dm = lastCompletedMonth();
  const [year, setYear] = useState(_dm.year);
  const [month, setMonth] = useState(_dm.month);
  const [semesterStart, setSemesterStart] = useState({ year: _dm.year, month: 1, length: 6 });
  const [semester, setSemester] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [exFestYear, setExFestYear] = useState(null);
  const [cedolinoPreviewOpen, setCedolinoPreviewOpen] = useState(false);
  const [absenceScope, setAbsenceScope] = useState("period");
  const [absenceSummary, setAbsenceSummary] = useState(null);
  const [absenceLoading, setAbsenceLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("monthly");
  const [search, setSearch] = useState("");

  const isAdmin = ["admin", "super_admin"].includes(effectiveRole);
  const isEmployee = effectiveRole === "employee";
  const periodEndLabel = `${String(new Date(year, month, 0).getDate()).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  const monthLabel = `${MONTHS_IT[month - 1]} ${year}`;

  useEffect(() => {
    announceShellCompany(
      user?.company_name || companyNameForShell(companies, companyId),
      companyId,
    );
  }, [companies, companyId, user?.company_name]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (isEmployee) { setEmployeeId(user.employee_id); return; }
        const params = { status: "all" };
        if (isAdmin && companyId) params.company_id = companyId;
        const { data } = await api.get("/employees", { params });
        setEmployees(data);
        // Se il dipendente selezionato non appartiene alla nuova lista, mantieni "Tutti".
        setEmployeeId((prev) => {
          if (prev === "__all__") return "__all__";
          const rec = reconcileEmployeeWithCompany(prev, data);
          return rec || "__all__";
        });
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}`);
        setCompanyConfig(data);
        const len = data.compensation_period_months || 6;
        const sm = data.semester_start_month || 1;
        const s = currentSemesterStart(new Date(), sm, len);
        setSemesterStart({ year: s.year, month: s.month, length: len });
        setExFestYear(null);
      } catch (e) { /* ignore */ }
    })();
  }, [companyId]);

  const loadSemester = async () => {
    if (!employeeId || employeeId === "__all__") { setSemester(null); return; }
    try {
      const params = { employee_id: employeeId, start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length };
      if (exFestYear !== null) params.ex_fest_year = exFestYear;
      const { data } = await api.get("/attendance/semester", { params });
      setSemester(data);
    } catch (e) {
      // 409 legittimo: banca ore non gestita dall'azienda o non abilitata per il dipendente.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && ["bank_hours_disabled", "bank_hours_disabled_employee"].includes(detail?.code)) {
        setSemester({ __bank_off: true, __bank_off_message: detail.message }); return;
      }
      toast.error(apiErr(e));
    }
  };
  useEffect(() => { loadSemester(); /* eslint-disable-next-line */ }, [employeeId, semesterStart.year, semesterStart.month, semesterStart.length, exFestYear]);

  // Assenteismo: carica sintesi JSON con debounce + mantiene dati vecchi durante il refetch
  useEffect(() => {
    if (activeTab !== "absenteeism" || !companyId || isEmployee) return undefined;
    let cancelled = false;
    setAbsenceLoading(true);
    const handle = setTimeout(() => {
      (async () => {
        try {
          const { data } = await api.get("/reports/company/absenteeism/summary", {
            params: { company_id: companyId, year, month, scope: absenceScope },
          });
          if (!cancelled) setAbsenceSummary(data);
        } catch (e) {
          if (!cancelled) toast.error(apiErr(e));
        } finally {
          if (!cancelled) setAbsenceLoading(false);
        }
      })();
    }, 250); // debounce 250 ms per evitare fetch a raffica mentre l'utente cambia mese
    return () => { cancelled = true; clearTimeout(handle); };
  }, [activeTab, companyId, year, month, absenceScope, isEmployee]);

  const emp = employees.find((e) => e.id === employeeId);
  const empLabel = emp ? `${emp.last_name}_${emp.first_name}` : "dipendente";
  const isAll = employeeId === "__all__";

  // ------------------------------------------------------------------
  // Documenti sezione "Riepilogo aziendale" — categorizzati
  // ------------------------------------------------------------------
  const companyDocs = useMemo(() => {
    return [
      // Azienda e organico
      {
        group: "Azienda e organico",
        title: "Anagrafica azienda e organico",
        icon: ContactRound,
        description: "CCNL, codici CNEL, posizioni INPS/INAIL/Cassa Edile · organico attivi e cessati.",
        meta: "Attivi + cessati",
        testid: "doc-anagraphics",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
            "data-testid": "download-anagraphics-pdf",
            onClick: () => downloadBlob("/exports/anagrafica-organico/pdf", { company_id: companyId, status: "all" }, "anagrafica-azienda-organico.pdf") },
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-anagraphics-excel",
            onClick: () => downloadBlob("/exports/anagrafica-organico/excel", { company_id: companyId, status: "all" }, "anagrafica-azienda-organico.xlsx") },
        ],
      },
      {
        group: "Azienda e organico",
        title: "Dossier azienda per audit",
        icon: FileCheck2,
        description: "PDF unico integrato: anagrafica + presenze del periodo + scadenze 81/2008. Pronto per ispezioni ATS / INAIL / ITL.",
        meta: `${monthLabel} · frontespizio + indice`,
        tag: "Audit",
        testid: "doc-audit-dossier",
        actions: [
          { key: "pdf", label: "Scarica dossier", icon: FileText, variant: "primary", disabled: !companyId,
            "data-testid": "download-audit-dossier-pdf",
            onClick: () => downloadBlob(
              "/reports/company/audit-dossier/pdf",
              { company_id: companyId, year, month, include_absenteeism: true },
              `dossier-audit_${year}-${String(month).padStart(2, "0")}.pdf`
            ) },
        ],
      },

      // Presenze e contatori
      {
        group: "Presenze e contatori",
        title: "Riepilogo aziendale",
        icon: TableProperties,
        description: "Prospetto mensile complessivo di presenze e causali per tutta l’azienda.",
        meta: monthLabel,
        testid: "doc-company-month",
        actions: [
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-company-excel",
            onClick: () => downloadBlob("/reports/company/month/excel", { company_id: companyId, year, month }, `azienda_${year}-${String(month).padStart(2, "0")}.xlsx`) },
        ],
      },
      {
        group: "Presenze e contatori",
        title: "Consuntivo contatori",
        icon: WalletCards,
        description: "Ferie, ROL ed ex festività maturati, utilizzati e residui fino al periodo selezionato.",
        meta: `Al ${periodEndLabel}`,
        testid: "doc-counters",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
            "data-testid": "download-counters-period-pdf",
            onClick: () => downloadBlob("/reports/company/counters-period/pdf", { company_id: companyId, year, month }, `contatori-al-${year}-${String(month).padStart(2, "0")}.pdf`) },
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-counters-period-excel",
            onClick: () => downloadBlob("/reports/company/counters-period/excel", { company_id: companyId, year, month }, `contatori-al-${year}-${String(month).padStart(2, "0")}.xlsx`) },
        ],
      },
      {
        group: "Presenze e contatori",
        title: "Contatori fine anno",
        icon: CalendarCheck2,
        description: "Proiezione previsionale dei contatori contrattuali alla chiusura dell’anno.",
        meta: `Previsionale al 31/12/${year}`,
        testid: "doc-year-end",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
            "data-testid": "download-year-end-pdf",
            onClick: () => downloadBlob("/reports/company/year-end/pdf", { company_id: companyId, year }, `contatori-fine-anno_${year}.pdf`) },
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-year-end-excel",
            onClick: () => downloadBlob("/reports/company/year-end/excel", { company_id: companyId, year }, `contatori-fine-anno_${year}.xlsx`) },
        ],
      },
      {
        group: "Presenze e contatori",
        title: "Permessi studio",
        icon: GraduationCap,
        description: "Ore utilizzate da ciascun dipendente e residuo sul limite annuale contrattuale.",
        meta: `Consuntivo al ${periodEndLabel}`,
        testid: "doc-study-leave",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
            "data-testid": "download-study-leave-pdf",
            onClick: () => downloadBlob("/reports/company/study-leave/pdf", { company_id: companyId, year, month }, `permessi-studio-al-${year}-${String(month).padStart(2, "0")}.pdf`) },
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-study-leave-excel",
            onClick: () => downloadBlob("/reports/company/study-leave/excel", { company_id: companyId, year, month }, `permessi-studio-al-${year}-${String(month).padStart(2, "0")}.xlsx`) },
        ],
      },

      // Elaborazione paghe
      {
        group: "Elaborazione paghe",
        title: "Tracciato cedolino",
        icon: ReceiptText,
        description: "Anteprima e generazione del file CSV per l’elaborazione paghe (Buffetti e altri).",
        meta: monthLabel,
        testid: "doc-cedolino",
        actions: [
          { key: "open", label: "Apri esportazione", icon: FileSpreadsheet, variant: "outline", disabled: !companyId,
            "data-testid": "open-cedolino-preview-btn",
            onClick: () => setCedolinoPreviewOpen(true) },
        ],
      },
    ];
  }, [companyId, year, month, monthLabel, periodEndLabel]);

  // ------------------------------------------------------------------
  // Documenti sezione "Sicurezza sul lavoro" (D.Lgs. 81/2008)
  // ------------------------------------------------------------------
  const safetyDocs = useMemo(() => {
    const list = [
      {
        title: "Riepilogo scadenze aziendale",
        icon: ShieldCheck,
        description: "Sorveglianza sanitaria, formazione, ruoli e attrezzature. Storico completo per stato (regolare / in scadenza / scaduta).",
        meta: `${MONTHS_IT[month - 1]} ${year}`,
        testid: "doc-safety-81",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
            "data-testid": "download-safety-81-pdf",
            onClick: () => downloadBlob("/safety/export/pdf", { company_id: companyId }, `scadenze_81_${year}-${String(month).padStart(2, "0")}.pdf`) },
          { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
            "data-testid": "download-safety-81-excel",
            onClick: () => downloadBlob("/safety/export/excel", { company_id: companyId }, `scadenze_81_${year}-${String(month).padStart(2, "0")}.xlsx`) },
        ],
      },
      {
        title: "Scheda sicurezza dipendente",
        icon: UserSearch,
        description: "Storico completo delle scadenze 81/2008 del dipendente selezionato in barra filtri.",
        meta: employeeId && employeeId !== "__all__"
          ? (emp ? `${emp.last_name} ${emp.first_name}` : "Dipendente selezionato")
          : "Seleziona un dipendente in alto",
        testid: "doc-safety-81-employee",
        actions: [
          { key: "pdf", label: "PDF", icon: FileText, variant: "secondary",
            disabled: !companyId,
            "data-testid": "download-safety-81-employee-pdf",
            onClick: () => {
              if (!employeeId || employeeId === "__all__") {
                toast.info("Seleziona prima un dipendente dalla barra filtri in alto");
                return;
              }
              return downloadBlob(
                "/safety/export/pdf",
                { company_id: companyId, employee_id: employeeId },
                `sicurezza_${(emp?.last_name || "dip")}_${(emp?.first_name || "")}.pdf`
              );
            },
          },
        ],
      },
    ];
    const q = search.trim().toLowerCase();
    return q ? list.filter((d) => (d.title + " " + d.description).toLowerCase().includes(q)) : list;
  }, [companyId, employeeId, emp, year, month, search]);

  const companyDocGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filter = q
      ? (d) => (d.title + " " + d.description + " " + d.group).toLowerCase().includes(q)
      : () => true;
    const map = new Map();
    for (const d of companyDocs) {
      if (!filter(d)) continue;
      if (!map.has(d.group)) map.set(d.group, []);
      map.get(d.group).push(d);
    }
    return Array.from(map.entries());
  }, [companyDocs, search]);

  // ------------------------------------------------------------------
  // Documenti sezione "Report mensile"
  // ------------------------------------------------------------------
  const monthlyDocs = useMemo(() => {
    const _company = companies.find((c) => c.id === companyId);
    const _mode = _company?.consultant_report_mode || "consuntivo_effettivo";
    const _modeLabel = _mode === "orario_teorico_eccezioni" ? "Orario teorico + eccezioni" : "Consuntivo effettivo";
    const list = [
      {
        title: "Cartellino mensile",
        icon: CalendarCheck2,
        description: "Presenze, assenze, ore lavorate e totali del dipendente.",
        meta: monthLabel,
        testid: "doc-monthly-cartellino",
        actions: isAll
          ? [
              { key: "pdf-all", label: "PDF · Tutti", icon: FileText, variant: "primary", disabled: !companyId,
                "data-testid": "download-monthly-pdf-all",
                onClick: async () => {
                  toast.info("Generazione PDF di tutti i dipendenti in corso…");
                  try {
                    await downloadBlob("/reports/company/month/all-employees-pdf", { company_id: companyId, year, month }, `presenze_TUTTI_${year}-${String(month).padStart(2, "0")}.pdf`);
                    toast.success("PDF generato correttamente");
                  } catch (e) { toast.error(apiErr(e)); }
                } },
            ]
          : [
              { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !employeeId,
                "data-testid": "download-monthly-pdf",
                onClick: () => downloadBlob("/reports/employee/month/pdf", { employee_id: employeeId, year, month }, `presenze_${empLabel}_${year}-${String(month).padStart(2, "0")}.pdf`) },
              { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !employeeId,
                "data-testid": "download-monthly-excel",
                onClick: () => downloadBlob("/reports/employee/month/excel", { employee_id: employeeId, year, month }, `presenze_${empLabel}_${year}-${String(month).padStart(2, "0")}.xlsx`) },
            ],
      },
    ];
    if (!isEmployee) {
      list.push(
        {
          title: "Foglio presenze consulente",
          icon: ClipboardList,
          description: "Prospetto mensile pronto per l’elaborazione paghe (tutta l’azienda).",
          meta: monthLabel,
          tag: _modeLabel,
          testid: "doc-monthly-presence-sheet",
          actions: [
            { key: "pdf", label: "PDF", icon: FileText, variant: "secondary", disabled: !companyId,
              "data-testid": "download-monthly-presence-sheet-pdf",
              onClick: () => downloadBlob("/reports/company/presence-sheet/pdf", { company_id: companyId, year, month }, `presenze_${year}-${String(month).padStart(2, "0")}.pdf`) },
          ],
        },
        {
          title: "Riepilogo aziendale",
          icon: TableProperties,
          description: "Totali mensili aziendali per causale e per dipendente.",
          meta: monthLabel,
          testid: "doc-monthly-company",
          actions: [
            { key: "xlsx", label: "Excel", icon: FileSpreadsheet, disabled: !companyId,
              "data-testid": "download-monthly-company-excel",
              onClick: () => downloadBlob("/reports/company/month/excel", { company_id: companyId, year, month }, `azienda_${year}-${String(month).padStart(2, "0")}.xlsx`) },
          ],
        },
        {
          title: "Tracciato cedolino",
          icon: ReceiptText,
          description: "File CSV per elaborazione paghe (Buffetti e altri).",
          meta: monthLabel,
          testid: "doc-monthly-cedolino",
          actions: [
            { key: "open", label: "Apri esportazione", icon: FileSpreadsheet, variant: "outline", disabled: !companyId,
              "data-testid": "open-monthly-cedolino-btn",
              onClick: () => setCedolinoPreviewOpen(true) },
          ],
        },
      );
    }
    const q = search.trim().toLowerCase();
    return q ? list.filter((d) => (d.title + " " + d.description).toLowerCase().includes(q)) : list;
  }, [companies, companyId, employeeId, empLabel, isAll, isEmployee, month, year, monthLabel, search]);

  // ------------------------------------------------------------------
  // Filtri comuni (barra unica)
  // ------------------------------------------------------------------
  const showEmployeeFilter = !isEmployee && ["monthly", "absenteeism", "semester", "safety"].includes(activeTab);
  const showPeriodFilter = true;
  const showCompanyFilter = isAdmin;

  const tabs = NAV_TABS.filter((t) => !(isEmployee && t.hideEmployee));

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="reports-page">
      {/* ---------- Intestazione ---------- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-suite-primary">
            <FileText size={12} />
            <span>Pianificazione e controlli</span>
          </div>
          <h1 className="mt-1 text-[26px] font-bold tracking-tight text-suite-neutral-900" style={{ fontFamily: "Manrope" }}>
            Report e Stampe
          </h1>
          <p className="text-[13px] text-suite-neutral-500 mt-0.5 max-w-2xl">
            Consulta, filtra ed esporta i dati relativi a dipendenti, presenze e andamento aziendale.
          </p>
        </div>
        <div className="relative w-full max-w-[380px]" data-testid="reports-search">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-suite-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca report, documento o funzione…"
            className="h-10 pl-9 bg-white border-suite-neutral-200 focus-visible:ring-suite-primary/25"
            data-testid="reports-search-input"
          />
        </div>
      </div>

      {/* ---------- Navigazione principale (compatta) ---------- */}
      <div
        className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-suite-neutral-200 bg-white p-1 shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        role="tablist"
        data-testid="reports-nav"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              onClick={() => setActiveTab(t.key)}
              data-testid={`nav-${t.key}`}
              className={[
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-all",
                active
                  ? "bg-suite-primary-light text-suite-primary border border-suite-primary/40 shadow-[inset_0_0_0_1px_rgba(76,175,80,0.15)]"
                  : "text-suite-neutral-600 border border-transparent hover:text-suite-primary hover:bg-suite-primary-light/40",
              ].join(" ")}
            >
              <Icon size={14} strokeWidth={active ? 2.2 : 1.8} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ---------- Filtri comuni ---------- */}
      <SuiteCard padding="sm" testid="reports-filters-card">
        <div className="flex flex-wrap items-end gap-3">
          {showCompanyFilter && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Azienda</Label>
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger className="h-9 w-56" data-testid="reports-company-select">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {showEmployeeFilter && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Dipendente</Label>
              <SuiteEmployeePicker
                employees={activeTab === "safety" ? employees : filterActiveInMonth(employees, year, month)}
                value={employeeId}
                onValueChange={setEmployeeId}
                includeAll
                className="w-72"
                testId="reports-employee-select"
                renderLabel={(e) => (
                  <>
                    {e.last_name} {e.first_name}
                    {e.cessation_date && <span className="text-suite-neutral-400 ml-2 text-xs">· {statusLabel(e)}</span>}
                  </>
                )}
              />
            </div>
          )}
          {showPeriodFilter && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Periodo</Label>
              <SuiteMonthPicker
                year={year}
                month={month}
                testid="reports-month-picker"
                onChange={({ year: nextYear, month: nextMonth }) => {
                  setYear(nextYear); setMonth(nextMonth);
                }}
              />
            </div>
          )}
          {activeTab === "absenteeism" && !isEmployee && (
            <div className="flex items-end gap-1.5">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Intervallo</Label>
                <Select value={absenceScope} onValueChange={setAbsenceScope}>
                  <SelectTrigger className="h-9 w-56" data-testid="absence-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="period">1 gennaio → {MONTHS_IT[month - 1]} {year}</SelectItem>
                    <SelectItem value="annual">Intero anno {year}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {activeTab === "semester" && !isEmployee && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Inizio periodo</Label>
                <SuiteMonthPicker
                  year={semesterStart.year}
                  month={semesterStart.month}
                  testid="reports-semester-month-picker"
                  onChange={({ year: nextYear, month: nextMonth }) => {
                    setSemesterStart({ ...semesterStart, year: nextYear, month: nextMonth });
                  }}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Durata</Label>
                <Select
                  value={String(semesterStart.length)}
                  onValueChange={(v) => setSemesterStart({ ...semesterStart, length: Number(v) })}
                >
                  <SelectTrigger className="h-9 w-48" data-testid="semester-length-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPENSATION_PERIODS.map((p) => (
                      <SelectItem key={p.months} value={String(p.months)}>{p.label} ({p.months} {p.months === 1 ? "mese" : "mesi"})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </SuiteCard>

      {/* ================================================================== */}
      {/* Report mensile                                                     */}
      {/* ================================================================== */}
      {activeTab === "monthly" && (
        <SuiteCard padding="sm" testid="reports-monthly-card">
          <div className="space-y-3">
            <SectionHeader
              icon={CalendarCheck2}
              title="Documenti mensili"
              subtitle={isAll
                ? `${monthLabel} · Tutti i dipendenti · Scegli il documento e il formato`
                : `${monthLabel} · ${emp ? `${emp.last_name} ${emp.first_name}` : "Seleziona un dipendente"}`}
            />
            <div className="space-y-1.5">
              {monthlyDocs.length === 0 && (
                <div className="rounded-lg border border-dashed border-suite-neutral-300 bg-suite-neutral-50/50 px-3 py-6 text-center text-[12px] text-suite-neutral-500">
                  Nessun documento corrisponde alla ricerca.
                </div>
              )}
              {monthlyDocs.map((d) => <DocRow key={d.title} {...d} />)}
            </div>
          </div>
        </SuiteCard>
      )}

      {/* ================================================================== */}
      {/* Riepilogo aziendale                                                */}
      {/* ================================================================== */}
      {activeTab === "company" && !isEmployee && (
        <div className="space-y-3">
          {companyDocGroups.length === 0 && (
            <SuiteCard padding="sm">
              <div className="rounded-lg border border-dashed border-suite-neutral-300 bg-suite-neutral-50/50 px-3 py-8 text-center text-[12px] text-suite-neutral-500">
                Nessun documento corrisponde alla ricerca.
              </div>
            </SuiteCard>
          )}
          {companyDocGroups.map(([groupName, docs]) => (
            <SuiteCard key={groupName} padding="sm" testid={`docgroup-${groupName.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="space-y-2">
                <div className="flex items-center gap-2 border-b border-suite-neutral-100 pb-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-suite-primary">{groupName}</div>
                  <div className="text-[10px] text-suite-neutral-400">· {docs.length} document{docs.length === 1 ? "o" : "i"}</div>
                </div>
                <div className="space-y-1.5">
                  {docs.map((d) => <DocRow key={d.title} {...d} />)}
                </div>
              </div>
            </SuiteCard>
          ))}
        </div>
      )}

      {/* ================================================================== */}
      {/* Assenteismo e presenza                                             */}
      {/* ================================================================== */}
      {activeTab === "absenteeism" && !isEmployee && (
        <AbsenteeismSection
          summary={absenceSummary}
          loading={absenceLoading}
          companyId={companyId}
          year={year}
          month={month}
          scope={absenceScope}
        />
      )}

      {/* ================================================================== */}
      {/* Semestre banca ore                                                 */}
      {/* ================================================================== */}
      {activeTab === "semester" && (
        <SemesterSection
          companyId={companyId}
          employeeId={employeeId}
          empLabel={empLabel}
          user={user}
          semesterStart={semesterStart}
          semester={semester}
          companyConfig={companyConfig}
          exFestYear={exFestYear}
          setExFestYear={setExFestYear}
        />
      )}

      {/* ================================================================== */}
      {/* Sicurezza sul lavoro (D.Lgs. 81/2008)                              */}
      {/* ================================================================== */}
      {activeTab === "safety" && !isEmployee && (
        <SuiteCard padding="sm" testid="reports-safety-card">
          <div className="space-y-3">
            <SectionHeader
              icon={ShieldCheck}
              title="Sicurezza sul lavoro · D.Lgs. 81/2008"
              subtitle={
                employeeId && employeeId !== "__all__" && emp
                  ? `Export aziendale e scheda del dipendente ${emp.last_name} ${emp.first_name}`
                  : "Esporta l’intera anagrafica scadenze aziendale, oppure seleziona un dipendente per la scheda individuale"
              }
            />
            <div className="space-y-1.5">
              {safetyDocs.length === 0 && (
                <div className="rounded-lg border border-dashed border-suite-neutral-300 bg-suite-neutral-50/50 px-3 py-6 text-center text-[12px] text-suite-neutral-500">
                  Nessun documento corrisponde alla ricerca.
                </div>
              )}
              {safetyDocs.map((d) => <DocRow key={d.title} {...d} />)}
            </div>
            <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-1.5 text-[10.5px] text-suite-neutral-500">
              Per gestire (aggiungere/modificare/rinnovare) le scadenze usa la pagina dedicata{" "}
              <a href="/scadenze-81" className="font-semibold text-suite-primary hover:underline">
                Pianificazione › Scadenze 81/2008
              </a>.
            </div>
          </div>
        </SuiteCard>
      )}

      <CedolinoPreviewDialog
        open={cedolinoPreviewOpen}
        onOpenChange={setCedolinoPreviewOpen}
        companyId={companyId}
        year={year}
        month={month}
        onDownload={(tracciato) => downloadBlob(
          "/reports/company/cedolino/csv",
          { company_id: companyId, year, month, tracciato: tracciato || "buffetti" },
          `cedolino_${tracciato || "buffetti"}_${year}-${String(month).padStart(2, "0")}.csv`
        )}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Confronto tra periodo corrente e periodo precedente                        */
/* -------------------------------------------------------------------------- */
function PeriodComparison({ current, previous, comparison, causesOrdered }) {
  if (!current) return null;
  if (!previous || !comparison) {
    return (
      <SuiteCard padding="sm">
        <div className="text-[11.5px] text-suite-neutral-500 italic">
          Confronto con il periodo precedente non disponibile (dati insufficienti).
        </div>
      </SuiteCard>
    );
  }
  const curLabel = periodShortLabel(current.period);
  const prevLabel = periodShortLabel(previous.period);

  // Bar chart pairs (current vs previous) per causale
  const maxPair = Math.max(
    1,
    ...causesOrdered.flatMap((c) => [
      current.by_cause?.[c.key] || 0,
      previous.by_cause?.[c.key] || 0,
    ]),
  );

  return (
    <SuiteCard padding="sm" testid="period-comparison-card">
      <SectionHeader
        icon={LineChart}
        title="Confronto con il periodo precedente"
        subtitle={`${curLabel} vs ${prevLabel} · variazione percentuale e delta assoluto`}
      />
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {/* Delta KPI */}
        <div className="grid grid-cols-2 gap-2" data-testid="comparison-deltas">
          <DeltaKpi label="% Assenteismo"
                    current={current.kpi.absence_pct}
                    previous={previous.kpi.absence_pct}
                    delta={comparison.absence_pct_delta}
                    unit=" pt"
                    negativeIsGood
                    testid="delta-absence-pct" />
          <DeltaKpi label="% Presenza"
                    current={current.kpi.presence_pct}
                    previous={previous.kpi.presence_pct}
                    delta={comparison.presence_pct_delta}
                    unit=" pt"
                    testid="delta-presence-pct" />
          <DeltaKpi label="Ore assenza"
                    current={current.kpi.absent}
                    previous={previous.kpi.absent}
                    delta={comparison.absent_delta_pct}
                    hours
                    unit=" %"
                    negativeIsGood
                    testid="delta-absent" />
          <DeltaKpi label="Ore lavorate"
                    current={current.kpi.worked}
                    previous={previous.kpi.worked}
                    delta={comparison.worked_delta_pct}
                    hours
                    unit=" %"
                    testid="delta-worked" />
          <DeltaKpi label="Episodi"
                    current={current.kpi.episodes}
                    previous={previous.kpi.episodes}
                    delta={comparison.episodes_delta}
                    absolute
                    negativeIsGood
                    testid="delta-episodes" />
          <DeltaKpi label="Giorni assenza"
                    current={current.kpi.absence_days}
                    previous={previous.kpi.absence_days}
                    delta={comparison.absence_days_delta}
                    absolute
                    negativeIsGood
                    testid="delta-absence-days" />
        </div>

        {/* Grafico appaiato per causale */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-suite-primary mb-2">
            Ore per causale · confronto
          </div>
          <div className="space-y-2">
            {causesOrdered.map((c) => {
              const cur = current.by_cause?.[c.key] || 0;
              const pre = previous.by_cause?.[c.key] || 0;
              const dot = pre > 0 ? ((cur - pre) / pre) * 100 : (cur > 0 ? 100 : 0);
              const trendColor = cur === pre ? "text-suite-neutral-500"
                : cur > pre ? "text-red-600" : "text-emerald-700";
              return (
                <div key={c.key} data-testid={`cmp-cause-${c.key}`}>
                  <div className="flex items-center justify-between text-[11px] font-medium text-suite-neutral-700">
                    <span>{c.label}</span>
                    <span className={`font-mono text-[11px] ${trendColor}`}>
                      {cur === pre ? "—" : `${cur > pre ? "+" : ""}${dot.toFixed(1)}%`}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className="w-16 text-[10px] text-suite-neutral-500">Prec.</span>
                    <div className="flex-1"><Bar value={pre} max={maxPair} color="bg-suite-neutral-300" /></div>
                    <span className="w-16 text-right font-mono text-[10.5px] text-suite-neutral-500">{fmtHours(pre)} h</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className="w-16 text-[10px] font-semibold text-suite-primary">Attuale</span>
                    <div className="flex-1"><Bar value={cur} max={maxPair} color={c.color} /></div>
                    <span className="w-16 text-right font-mono text-[10.5px] text-suite-neutral-800">{fmtHours(cur)} h</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SuiteCard>
  );
}

function periodShortLabel(period) {
  if (!period) return "—";
  if (period.scope === "annual") return `Anno ${period.year}`;
  const m = MONTHS_IT[(period.month || 1) - 1];
  return `${m} ${period.year}`;
}

function DeltaKpi({ label, current, previous, delta, unit = "", absolute = false, negativeIsGood = false, hours = false, testid }) {
  const fmtVal = hours ? fmtHours : fmtNum;
  const hasDelta = delta !== null && delta !== undefined;
  const isFlat = hasDelta && Math.abs(delta) < 0.005;
  const isPositive = hasDelta && delta > 0;
  const isNegative = hasDelta && delta < 0;
  const good = negativeIsGood ? isNegative : isPositive;
  const bad = negativeIsGood ? isPositive : isNegative;

  const tone = !hasDelta || isFlat
    ? "border-suite-neutral-200 bg-white text-suite-neutral-700"
    : good
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : bad
        ? "border-red-200 bg-red-50 text-red-800"
        : "border-suite-neutral-200 bg-white text-suite-neutral-700";

  const arrow = isFlat ? "—" : isPositive ? "▲" : isNegative ? "▼" : "—";
  const deltaStr = hasDelta
    ? (absolute
        ? `${isPositive ? "+" : ""}${delta}${unit}`
        : `${isPositive ? "+" : ""}${Number(delta).toFixed(2)}${unit}`)
    : "n/d";

  return (
    <div className={`rounded-lg border px-2.5 py-2 ${tone}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70 truncate">{label}</div>
        <span className="text-[13px]">{arrow}</span>
      </div>
      <div className="mt-1 font-mono text-[15px] font-bold leading-none">
        {typeof current === "number" ? fmtVal(current) : (current ?? "—")}
      </div>
      <div className="mt-0.5 text-[10.5px] opacity-75 flex items-center gap-1.5">
        <span>vs {typeof previous === "number" ? fmtVal(previous) : (previous ?? "—")}</span>
        <span className="text-[10.5px] font-semibold">{deltaStr}</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione Assenteismo & Presenza                                             */
/* -------------------------------------------------------------------------- */
function AbsenteeismSection({ summary, loading, companyId, year, month, scope }) {
  const suffix = scope === "annual" ? "annuale" : `al-${year}-${String(month).padStart(2, "0")}`;

  const exportPdf = () => downloadBlob(
    "/reports/company/absenteeism/pdf",
    { company_id: companyId, year, month, scope },
    `assenteismo-${suffix}.pdf`
  );
  const exportXlsx = () => downloadBlob(
    "/reports/company/absenteeism/excel",
    { company_id: companyId, year, month, scope },
    `assenteismo-${suffix}.xlsx`
  );

  const kpi = summary?.kpi;
  const byCause = summary?.by_cause || {};
  const causesOrdered = [
    { key: "ferie", label: "Ferie", color: "bg-emerald-500" },
    { key: "permessi", label: "Permessi / ROL", color: "bg-amber-500" },
    { key: "malattia", label: "Malattia", color: "bg-red-500" },
    { key: "legge104", label: "Legge 104", color: "bg-indigo-500" },
    { key: "congedi", label: "Congedi", color: "bg-sky-500" },
    { key: "altre", label: "Altre", color: "bg-slate-400" },
  ];
  const maxCause = Math.max(1, ...Object.values(byCause));
  const rows = summary?.rows || [];
  const most = summary?.ranking?.most_present || [];
  const worst = summary?.ranking?.most_absent || [];

  return (
    <div className="space-y-3">
      <SuiteCard padding="sm">
        <SectionHeader
          icon={Activity}
          title="Analisi assenteismo e presenza"
          subtitle="Confronto omogeneo sull'orario teorico. Festività e Santo Patrono esclusi dal calcolo."
          actions={
            <>
              <SuiteButton
                size="sm" variant="secondary" icon={FileText}
                disabled={!companyId}
                onClick={exportPdf}
                data-testid="download-absenteeism-pdf"
              >
                Esporta PDF
              </SuiteButton>
              <SuiteButton
                size="sm" icon={FileSpreadsheet}
                disabled={!companyId}
                onClick={exportXlsx}
                data-testid="download-absenteeism-excel"
              >
                Esporta Excel
              </SuiteButton>
            </>
          }
        />
      </SuiteCard>

      {loading && !kpi && (
        <SuiteCard padding="sm">
          <div className="py-6 text-center text-[12px] text-suite-neutral-500">Caricamento analisi…</div>
        </SuiteCard>
      )}
      {loading && kpi && (
        <div className="rounded-md border border-suite-primary/25 bg-suite-primary-light/20 px-3 py-1.5 text-[11.5px] text-suite-primary flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-suite-primary animate-pulse" />
          Aggiornamento in corso… i dati mostrati sono quelli del periodo precedentemente caricato.
        </div>
      )}

      {kpi && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Dipendenti" value={kpi.employees} icon={Users} testid="kpi-employees" />
            <Kpi label="Ore teoriche" value={fmtHours(kpi.theoretical)} unit="h" icon={CalendarDays} testid="kpi-theoretical" />
            <Kpi label="Ore lavorate" value={fmtHours(kpi.worked)} unit="h" tone="good" icon={TrendingUp} testid="kpi-worked" />
            <Kpi label="Ore assenza" value={fmtHours(kpi.absent)} unit="h" tone="warn" icon={TrendingDown} testid="kpi-absent" />
            <Kpi label="% Presenza" value={fmtNum(kpi.presence_pct)} unit="%" tone="primary" icon={LineChart} testid="kpi-presence-pct" />
            <Kpi label="% Assenteismo" value={fmtNum(kpi.absence_pct)} unit="%" tone={kpi.absence_pct >= 5 ? "bad" : "warn"} icon={BarChart3} testid="kpi-absence-pct" />
            <Kpi label="Episodi · Giorni" value={`${kpi.episodes} · ${kpi.absence_days}`} icon={CalendarCheck2} testid="kpi-episodes" />
          </div>

          {/* Confronto con periodo precedente */}
          <PeriodComparison
            current={summary}
            previous={summary?.previous}
            comparison={summary?.comparison}
            causesOrdered={causesOrdered}
          />

          {/* Dettaglio causali + Graduatorie */}
          <div className="grid gap-3 lg:grid-cols-3">
            <SuiteCard padding="sm">
              <SectionHeader icon={BarChart3} title="Dettaglio per causale" subtitle="Totale ore aggregate per gruppo di causale" />
              <div className="mt-3 space-y-2">
                {causesOrdered.map((c) => (
                  <div key={c.key} data-testid={`cause-${c.key}`}>
                    <div className="flex items-center justify-between text-[11.5px] font-medium text-suite-neutral-700">
                      <span>{c.label}</span>
                      <span className="font-mono text-[11.5px] text-suite-neutral-600">{fmtHours(byCause[c.key] || 0)} h</span>
                    </div>
                    <Bar value={byCause[c.key] || 0} max={maxCause} color={c.color} />
                  </div>
                ))}
              </div>
            </SuiteCard>

            <SuiteCard padding="sm">
              <SectionHeader icon={TrendingUp} title="Graduatoria presenze" subtitle="Migliori percentuali di presenza" />
              <ul className="mt-3 divide-y divide-suite-neutral-100" data-testid="ranking-most-present">
                {most.length === 0 && <li className="py-3 text-[12px] text-suite-neutral-500">Dati non sufficienti.</li>}
                {most.map((r, i) => (
                  <li key={r.employee + i} className="flex items-center justify-between py-1.5 text-[12.5px]">
                    <span className="truncate text-suite-neutral-700">
                      <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-800">{i + 1}</span>
                      {r.employee}
                    </span>
                    <span className="font-mono font-semibold text-emerald-700">{fmtNum(r.presence_pct)}%</span>
                  </li>
                ))}
              </ul>
            </SuiteCard>

            <SuiteCard padding="sm">
              <SectionHeader icon={TrendingDown} title="Maggiore assenteismo" subtitle="Percentuali più elevate di assenza" />
              <ul className="mt-3 divide-y divide-suite-neutral-100" data-testid="ranking-most-absent">
                {worst.length === 0 && <li className="py-3 text-[12px] text-suite-neutral-500">Dati non sufficienti.</li>}
                {worst.map((r, i) => (
                  <li key={r.employee + i} className="flex items-center justify-between py-1.5 text-[12.5px]">
                    <span className="truncate text-suite-neutral-700">
                      <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-[10px] font-bold text-red-700">{i + 1}</span>
                      {r.employee}
                    </span>
                    <span className="font-mono font-semibold text-red-700">{fmtNum(r.absence_pct)}%</span>
                  </li>
                ))}
              </ul>
            </SuiteCard>
          </div>

          {/* Tabella dettagliata */}
          <SuiteCard padding="sm">
            <SectionHeader icon={TableProperties} title="Dettaglio dipendenti" subtitle="Ore teoriche, lavorate, assenze e indice di assenteismo per dipendente" />
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="absenteeism-table">
                <thead>
                  <tr className="border-b border-suite-neutral-200 text-left text-[10px] uppercase tracking-widest text-suite-neutral-500">
                    <th className="py-1.5 pr-2">Dipendente</th>
                    <th className="py-1.5 pr-2 text-right">Teor.</th>
                    <th className="py-1.5 pr-2 text-right">Lavor.</th>
                    <th className="py-1.5 pr-2 text-right">Ass.</th>
                    <th className="py-1.5 pr-2 text-right">GG</th>
                    <th className="py-1.5 pr-2 text-right">Epis.</th>
                    <th className="py-1.5 pr-2 text-right">Pres. %</th>
                    <th className="py-1.5 pr-2 text-right">Ass. %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-4 text-center text-suite-neutral-500 text-[12px]">Nessun dipendente nel periodo.</td></tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-suite-neutral-100 hover:bg-suite-neutral-50/60">
                      <td className="py-1.5 pr-2 text-suite-neutral-800">{r.employee}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtHours(r.theoretical)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtHours(r.worked)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtHours(r.absent)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{r.absence_days}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{r.episodes}</td>
                      <td className="py-1.5 pr-2 text-right font-mono text-emerald-700">{fmtNum(r.presence_pct)}%</td>
                      <td className={`py-1.5 pr-2 text-right font-mono ${r.absence_pct >= 5 ? "text-red-700" : "text-amber-700"}`}>{fmtNum(r.absence_pct)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-1.5 text-[10.5px] text-suite-neutral-500">
              Festività e Santo Patrono esclusi. Le ore sono confrontate sull’orario teorico per uniformare full-time e part-time.
            </div>
          </SuiteCard>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sezione Semestre banca ore                                                 */
/* -------------------------------------------------------------------------- */
function RateoPoolCard({ pool, label, unit = "h", tone = "amber", testid, counter = false }) {
  if (!pool) return null;
  const toneMap = {
    secondary: { border: "border-suite-secondary/30", bg: "bg-suite-secondary-light/40", label: "text-suite-secondary", value: "text-suite-secondary" },
    warning:   { border: "border-suite-warning/40",   bg: "bg-suite-warning-light/50",   label: "text-suite-warning",   value: "text-suite-warning" },
    amber:     { border: "border-amber-200",          bg: "bg-amber-50/60",              label: "text-amber-800",       value: "text-amber-900" },
  };
  const t = toneMap[tone] || toneMap.amber;
  const fmtVal = (v) => (counter ? formatCounterHours(v || 0) : unit === "h" ? fmtHours(v || 0) : fmtNum(v || 0));
  const residualPositive = Number(pool.residual || 0) >= 0;
  return (
    <div className={`border ${t.border} ${t.bg} rounded-md p-3`} data-testid={`${testid}-card`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${t.label} flex items-center gap-1.5 flex-wrap`}>
            {label}
            {pool.rolled_over && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-suite-primary-light text-suite-primary border border-suite-primary/30 font-semibold normal-case tracking-normal"
                    title={`Riportato dagli anni ${pool.base_year}→${pool.year - 1}`}
                    data-testid={`${testid}-rollover-badge`}>
                ⟲ Riportato
              </span>
            )}
          </div>
          <div className="text-[9.5px] text-suite-neutral-500 mt-0.5">Unità: {unit === "gg" ? "giorni" : "ore"}</div>
        </div>
        <div className={`text-[20px] font-mono font-bold whitespace-nowrap ${residualPositive ? t.value : "text-suite-error"}`}
             data-testid={`${testid}-residual`}>
          {fmtVal(pool.residual)} {unit}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-2.5 gap-y-1 text-[11px]">
        <div>
          <div className="text-[9.5px] text-suite-neutral-500 uppercase tracking-wider">Iniziale</div>
          <div className="font-mono font-semibold" data-testid={`${testid}-initial`}>{fmtVal(pool.initial)} {unit}</div>
        </div>
        <div>
          <div className="text-[9.5px] text-suite-neutral-500 uppercase tracking-wider">Rateo mensile</div>
          <div className="font-mono font-semibold" data-testid={`${testid}-monthly`}>{fmtVal(pool.monthly_accrual)} {unit}</div>
        </div>
        <div>
          <div className="text-[9.5px] text-suite-neutral-500 uppercase tracking-wider">Accant. anno</div>
          <div className="font-mono font-semibold" data-testid={`${testid}-accrued`}>{fmtVal(pool.accrued)} {unit}</div>
        </div>
        <div>
          <div className="text-[9.5px] text-suite-neutral-500 uppercase tracking-wider">Consumato</div>
          <div className="font-mono font-semibold" data-testid={`${testid}-consumed`}>{fmtVal(pool.consumed)} {unit}</div>
        </div>
      </div>
    </div>
  );
}

function SemesterSection({ companyId, employeeId, empLabel, user, semesterStart, semester, companyConfig, exFestYear, setExFestYear }) {
  // Prospettiva UI: eredita il ruolo effettivo dal Foundation Context Engine.
  const effectiveRole = useEffectiveRole();
  const isAll = employeeId === "__all__";
  const endMonthIndex = (semesterStart.month - 1 + semesterStart.length - 1) % 12;
  const endYear = semesterStart.year + Math.floor((semesterStart.month - 1 + semesterStart.length - 1) / 12);
  const periodLabel = `${MONTHS_IT[semesterStart.month - 1]} ${semesterStart.year} → ${MONTHS_IT[endMonthIndex]} ${endYear}`;

  const exportCompanyPdf = async () => {
    toast.info("Generazione PDF aziendale in corso…");
    try {
      await downloadBlob("/reports/company/bank-hours-semester/pdf",
        { company_id: companyId, start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length },
        `andamento-banca-ore_${semesterStart.year}-${String(semesterStart.month).padStart(2, "0")}.pdf`);
      toast.success("PDF banca ore generato");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const exportCompanyXlsx = async () => {
    toast.info("Generazione Excel aziendale in corso…");
    try {
      await downloadBlob("/reports/company/bank-hours-semester/excel",
        { company_id: companyId, start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length },
        `andamento-banca-ore_${semesterStart.year}-${String(semesterStart.month).padStart(2, "0")}.xlsx`);
      toast.success("Excel banca ore generato");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3">
      <SuiteCard padding="sm">
        <SectionHeader
          icon={WalletCards}
          title="Andamento banca ore"
          subtitle={`Periodo selezionato: ${periodLabel}`}
          actions={
            <>
              <SuiteButton size="sm" variant="secondary" icon={FileText}
                disabled={!companyId || effectiveRole === "employee"}
                onClick={exportCompanyPdf}
                data-testid="download-bank-hours-company-pdf">
                Esporta PDF
              </SuiteButton>
              <SuiteButton size="sm" icon={FileSpreadsheet}
                disabled={!companyId || effectiveRole === "employee"}
                onClick={exportCompanyXlsx}
                data-testid="download-bank-hours-company-excel">
                Esporta Excel
              </SuiteButton>
            </>
          }
        />
        {companyConfig && companyConfig.semester_start_month && semesterStart.length > 1 && ((semesterStart.month - companyConfig.semester_start_month) % semesterStart.length + semesterStart.length) % semesterStart.length !== 0 && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
            Attenzione: la configurazione azienda prevede il periodo di compensazione a partire da <b>{MONTHS_IT[companyConfig.semester_start_month - 1]}</b>.
          </div>
        )}
      </SuiteCard>

      {/* KPI banca ore dipendente */}
      {!isAll && semester?.__bank_off && (
        <SuiteCard padding="sm">
          <div className="text-[12px] text-suite-neutral-700" data-testid="semester-bank-off">
            {semester.__bank_off_message || "Banca ore non disponibile per questo dipendente."} I documenti mensili restano disponibili nella sezione Report mensile.
          </div>
        </SuiteCard>
      )}
      {!isAll && semester && !semester.__bank_off && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <Kpi label="Saldo iniziale" value={fmtHoursSigned(semester.initial_balance)} icon={CalendarDays} testid="kpi-semester-initial" />
            <Kpi label={`Maturato (${semester.months_with_data || 0}/${semester.length})`} value={fmtHoursSigned(semester.accumulated)} icon={TrendingUp} testid="kpi-semester-accrued" />
            <Kpi
              label={semester.semester_closed ? "Saldo finale (chiuso)" : "Saldo progressivo"}
              value={fmtHoursSigned(semester.final_balance)}
              tone={semester.final_balance >= 0 ? "good" : "bad"}
              icon={LineChart}
              testid="kpi-semester-final"
            />
          </div>

          {semester.semester_closed ? (
            <div className="grid gap-2 md:grid-cols-2">
              <Kpi label="Ore da pagare" value={fmtHours(semester.hours_to_pay)} unit="h" tone="good" icon={TrendingUp} hint="Saldo positivo · in busta paga" />
              <Kpi label="Ore da trattenere" value={fmtHours(semester.hours_to_recover)} unit="h" tone="bad" icon={TrendingDown} hint="Saldo negativo · recupero/trattenuta" />
            </div>
          ) : (
            <SuiteCard padding="sm">
              <div className="text-[12px] text-amber-800">
                <b>Periodo in corso ({compensationPeriod(semester.length).label.toLowerCase()}).</b> Il saldo verrà evidenziato come “da pagare” o “da trattenere” solo alla chiusura del {compensationPeriod(semester.length).noun},
                quando tutti i {semester.length} mesi avranno dati caricati. Attualmente il saldo positivo/negativo viene riportato al mese successivo.
              </div>
            </SuiteCard>
          )}

          {/* Sabati */}
          {semester?.saturday_quota_annual?.quota_annual_days > 0 && (
            <SuiteCard padding="sm">
              <SectionHeader
                icon={CalendarCheck2}
                title={`Sabati ordinari — Anno ${semester.saturday_quota_annual.year}`}
                subtitle={`Quota mensile ${semester.saturday_quota_annual.quota_month} × ${semester.saturday_quota_annual.quota_hours_per_day} h · ${semester.saturday_quota_annual.quota_annual_days} sabati/anno`}
              />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Kpi label="Utilizzati" value={`${semester.saturday_quota_annual.used_days}/${semester.saturday_quota_annual.quota_annual_days}`} testid="sat-quota-used" />
                <Kpi label="Residui" value={`${semester.saturday_quota_annual.remaining_days} (${semester.saturday_quota_annual.remaining_hours} h)`} tone={semester.saturday_quota_annual.remaining_days > 0 ? "good" : "warn"} />
              </div>
              <div className="h-2 bg-sky-100 rounded overflow-hidden mt-3">
                <div className="h-full bg-sky-600 transition-all"
                     style={{ width: `${Math.min(100, (semester.saturday_quota_annual.used_days / Math.max(1, semester.saturday_quota_annual.quota_annual_days)) * 100)}%` }} />
              </div>
            </SuiteCard>
          )}

          {/* Ratei */}
          {(semester?.ferie_pool || semester?.rol_pool || semester?.ex_fest_pool) && (
            <SuiteCard padding="sm">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-suite-primary">
                    Ratei annui dipendente — Anno {semester.ex_fest_pool?.year || semester.ferie_pool?.year}
                  </div>
                  <div className="text-[11px] text-suite-neutral-500 mt-0.5">
                    Iniziale + accantonamento − consumato = residuo disponibile.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] uppercase tracking-widest text-suite-primary whitespace-nowrap">Anno pool</Label>
                  <Select value={String(exFestYear ?? semesterStart.year)} onValueChange={(v) => setExFestYear(Number(v))}>
                    <SelectTrigger className="w-24 h-8 text-sm" data-testid="ex-fest-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[semesterStart.year - 2, semesterStart.year - 1, semesterStart.year, semesterStart.year + 1].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                <RateoPoolCard pool={semester.ferie_pool}   label="Contatore Ferie"          unit={semester.ferie_pool?.unit === "hours" ? "h" : "gg"} tone="secondary" testid="ferie-pool" />
                <RateoPoolCard pool={semester.rol_pool}     label="Contatore ROL / Permessi" unit="h" counter  tone="warning"   testid="rol-pool" />
                <RateoPoolCard pool={semester.ex_fest_pool} label="Contatore Ex Festività"   unit="h" counter  tone="amber"     testid="ex-fest-pool" />
              </div>
            </SuiteCard>
          )}
        </>
      )}

      {/* Download singolo dipendente / tutti */}
      <SuiteCard padding="sm">
        <SectionHeader icon={Download} title="Esporta dati dipendente"
          subtitle={isAll ? "Excel semestrale di tutti i dipendenti" : "Excel del singolo dipendente selezionato"}
          actions={
            <>
              {isAll ? (
                <SuiteButton
                  size="sm" icon={Download}
                  onClick={async () => {
                    toast.info("Generazione Excel semestrale in corso…");
                    try {
                      await downloadBlob(
                        "/reports/company/semester/all-employees-excel",
                        { company_id: companyId, start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length },
                        `semestre_TUTTI_${semesterStart.year}-${String(semesterStart.month).padStart(2, "0")}.xlsx`
                      );
                      toast.success("Excel semestrale generato");
                    } catch (e) { toast.error(apiErr(e)); }
                  }}
                  disabled={!companyId}
                  data-testid="download-semester-excel-all"
                >
                  Excel · Tutti
                </SuiteButton>
              ) : (
                <SuiteButton
                  size="sm" icon={Download}
                  onClick={() => downloadBlob(
                    "/reports/employee/semester/excel",
                    { employee_id: employeeId, start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length },
                    `semestre_${empLabel}_${semesterStart.year}-${String(semesterStart.month).padStart(2, "0")}.xlsx`
                  )}
                  disabled={!employeeId}
                  data-testid="download-semester-excel"
                >
                  Excel dipendente
                </SuiteButton>
              )}
              <SuiteButton
                size="sm" variant="outline" icon={Printer}
                onClick={() => downloadBlob(
                  "/reports-legacy/semestrale/" + companyId,
                  { start_year: semesterStart.year, start_month: semesterStart.month, length: semesterStart.length },
                  `Tabulato-presenze_${semesterStart.year}-${String(semesterStart.month).padStart(2, "0")}.xlsx`
                )}
                disabled={!companyId || effectiveRole === "employee"}
                data-testid="download-tabulato-legacy"
              >
                Tabulato formato Studio
              </SuiteButton>
            </>
          }
        />
      </SuiteCard>
    </div>
  );
}
